import { Rectangle, Point } from 'pixi.js';
import { createTextWordMetrics, layoutTextWords } from './textMetricsHelpers';
import { TextLineMetrics } from './TextLineMetrics';
import type { WordStyle } from './WordStyle';
import type { WordToken } from './WordToken';
import type WordElementDataStore from './WordElementDataStore';

type CharOffsets = {
    /**
     * index of the line
     */
    lineIndex: number;
    /**
     * index of the word into the selected line
     */
    wordIndex: number;
    /**
     * index of the word element into the selected word
     */
    elementIndex: number;
    /**
     * index of the character into the selected element
     */
    characterIndex: number;
};

/**
 * Represents measurements of a provided block of text. Uses fontkit to compute
 * metrics for the text including positioning and dimensions.
 *
 * Note that the current implementation of TextAssetMetrics does not do any caching,
 * if performance is a concern there is opportunity to add caching.
 *
 */
export class TextAssetMetrics {
    classType = 'TextAssetMetrics';
    #textLineList: TextLineMetrics[] = [];
    #canDisplayAllText = true;

    /**
     * Once the TextMetric object is constructed it cannot be modified, a new object should
     * be created if new metrics are required.
     */
    constructor(lines: TextLineMetrics[]) {
        if (lines.some(l => !(l instanceof TextLineMetrics))) {
            throw new Error('TextAssetMetrics was initialized with invalid line data');
        }
        this.#textLineList = lines;

        // Note that setPosition must be called first
        this.#setPosition();
        this.#setAlignmentAndReadingDirection();
        this.#applyMaxHeight();
    }

    /**
     * Gets the number of lines.
     */
    get numLines(): number {
        return this.#textLineList.length;
    }

    /**
     * Indicates if all the text can fit within the provided max height in the style.
     */
    get canDisplayAllText(): boolean {
        return this.#canDisplayAllText;
    }

    /**
     * Return the style used in generating the metrics. Note it could either return
     * the fill or highlight style.
     */
    get style(): WordStyle {
        if (this.numLines === 0) {
            throw new Error('Cannot get text style, no text lines exist');
        }
        return this.#textLineList[0].style;
    }

    /**
     * Get the line metric objects.
     */
    get lines(): TextLineMetrics[] {
        return this.#textLineList;
    }

    /**
     * Get the string of text for the metrics (will have leading/trailing spaces removed)
     */
    get text(): string {
        return this.#textLineList.reduce((acc, line) => acc + line.text, '');
    }

    /**
     * The maximum width of all text lines.
     */
    get maxLineWidth(): number {
        return this.getBounds().width;
    }

    /**
     * The maximum line height of all text lines.
     */
    get maxLineHeight(): number {
        let maxLineHeight = 0;
        for (let i = 0; i < this.numLines; i++) {
            maxLineHeight = Math.max(maxLineHeight, this.getLineHeightFor(i));
        }
        return maxLineHeight;
    }

    /**
     * The maximum font height of all text lines. The font height is from the bottom of the
     * descent to the top of the ascent.
     */
    get maxFontHeight(): number {
        return this.#textLineList.reduce((max, line) => Math.max(max, line.fontHeight), 0);
    }

    /**
     * The maximum x-height of all text lines.
     */
    get maxXHeight(): number {
        return this.#textLineList.reduce((max, line) => Math.max(max, line.xHeight), 0);
    }

    /**
     * The maximum extent beyond the x-height for all text lines.
     */
    get maxExtentBeyondXHeight(): number {
        return this.#textLineList.reduce((max, line) => Math.max(max, line.extentBeyondXHeight), 0);
    }

    /**
     * The distance between baselines
     */
    get baselineSeparation(): number {
        // Add the leading to the balanced height for the baseline-to-baseline distance
        return this.balancedHeight + this.style.leading;
    }

    /**
     * The balanced height of the text, this finds the minimum height which allows for equal
     * spacing above and below the text. Specifically, it defines the height which fits the
     * x-height plus twice the maximum extent beyond the x-height.
     * Visually, this makes the text appear vertically centered, since the perceived
     * bottom reference is the baseline and the perceived top reference is at the x-height.
     */
    get balancedHeight(): number {
        return 2 * this.maxExtentBeyondXHeight + this.maxXHeight;
    }

    /**
     *  Computes the line height for a given line index.
     *  Line height is defined as the distance
     *  from the bottom of the line's descent to the bottom of the previous line's descent (or
     *  if there is no previous line then to the top of the line's ascent).
     */
    getLineHeightFor(lineIndex: number): number {
        if (this.numLines === 0) {
            return 0;
        }

        return this.lines[lineIndex].fontHeight + this.getSpacingAboveLine(lineIndex);
    }

    /**
     * Computes the spacing above the provided line.
     * The spacing is computed from the top of the line's ascent to the bottom of the previous
     * line's descent. If this line is the first line then the spacing is zero.
     */
    getSpacingAboveLine(lineIndex: number): number {
        if (this.numLines === 0 || lineIndex === 0) {
            return 0;
        }

        return this.lines[lineIndex].y - this.lines[lineIndex - 1].y - this.lines[lineIndex - 1].fontHeight;
    }

    /**
     * Computes the spacing below the provided line.
     * The spacing is computed from the bottom of the line's descent to the top of the next
     * line's ascent. If this line is the last line then the spacing is zero.
     */
    getSpacingBelowLine(lineIndex: number): number {
        if (this.numLines === 0 || lineIndex === this.numLines - 1) {
            return 0;
        }

        return this.lines[lineIndex + 1].y - this.lines[lineIndex].y - this.lines[lineIndex].fontHeight;
    }

    /**
     * Gets the overall bounding box of the text, where the origin is the top left corner.
     */
    getBounds(): Rectangle {
        if (this.numLines === 0) {
            return Rectangle.EMPTY;
        }
        const box = this.#textLineList[0].getBounds();
        for (let i = 1; i < this.numLines; i++) {
            box.enlarge(this.#textLineList[i].getBounds());
        }
        return box;
    }

    /**
     * Compute the x-distance into a word element that a provided char index is. Note that this
     * distance is from the word element's origin (and not the bounding box origin).
     *
     * @param lineIndex - the line index in which the char is
     * @param wordIndex - the word index into the line in which the char is
     * @param elementIndex - the word element index into the text word in which the char is
     * @param charIndex - the char index into the word element
     * @returns the location of the char relative to the start of its word element
     */
    positionOfCharRelativeToWordElement(lineIndex: number, wordIndex: number, elementIndex: number, charIndex: number): Point {
        const line = this.lines[lineIndex];
        if (!line) {
            return new Point(0, 0);
        }

        const textWord = line.textWords[wordIndex];
        if (!textWord) {
            return new Point(0, 0);
        }

        const wordElement = textWord.wordElements[elementIndex];
        if (!wordElement) {
            return new Point(0, 0);
        }

        return new Point(
            wordElement.partialAdvanceWidth(0, charIndex),
            0
        );
    }

    /**
     * Get the line, word, element and element character index for the provided global
     * character index.
     */
    getOffsetsForChar(charIndex: number): CharOffsets {
        let currIndex = 0;
        // Iterate over all lines
        for (let lineIndex = 0; lineIndex < this.lines.length; lineIndex++) {
            const line = this.lines[lineIndex];

            // Handle if a leading space was removed
            if (line.removedLeadingSpace) {
                if (currIndex === charIndex) {
                    return {
                        lineIndex,
                        wordIndex: 0,
                        elementIndex: 0,
                        characterIndex: 0,
                    };
                }
                currIndex++;
            }

            // Iterate over all words in this line
            for (let wordIndex = 0; wordIndex < line.textWords.length; wordIndex++) {
                const textWord = line.textWords[wordIndex];

                // Iterate over all word elements in this line
                for (let elementIndex = 0; elementIndex < textWord.wordElements.length; elementIndex++) {
                    const wordElement = textWord.wordElements[elementIndex];

                    // Iterate over all characters in this word element
                    for (let characterIndex = 0; characterIndex < wordElement.numChars; characterIndex++) {
                        if (currIndex === charIndex) {
                            return {
                                lineIndex,
                                wordIndex,
                                elementIndex,
                                characterIndex,
                            };
                        }
                        currIndex++;
                    }
                }
            }

            // Handle if a trailing space was removed
            if (line.removedTrailingSpace) {
                currIndex++;
                if (currIndex === charIndex) {
                    const wordIndex = line.textWords.length - 1;
                    const elementIndex = line.textWords[wordIndex].numWordElements - 1;
                    const characterIndex = line.textWords[wordIndex].wordElements[elementIndex].numChars - 1;
                    return {
                        lineIndex,
                        wordIndex,
                        elementIndex,
                        characterIndex,
                    };
                }
            }
        }

        // Fallback to after the last character
        let lineIndex = 0;
        let wordIndex = 0;
        let elementIndex = 0;
        let characterIndex = 0;
        if (this.lines.length > 0) {
            lineIndex = this.lines.length - 1;
            const numWordsLastLine = this.lines[lineIndex].textWords.length;
            if (numWordsLastLine > 0) {
                wordIndex = numWordsLastLine - 1;
                const numElementsLastWord = this.lines[lineIndex].textWords[wordIndex].wordElements.length;
                if (numElementsLastWord > 0) {
                    elementIndex = numElementsLastWord - 1;
                    characterIndex = this.lines[lineIndex].textWords[wordIndex].wordElements[elementIndex].numChars;
                }
            }
        }

        return {
            lineIndex,
            wordIndex,
            elementIndex,
            characterIndex,
        };
    }

    /**
     * Sets the position of the underlying word elements so that the bounding boxes can be
     * computed correctly.
     * Note that text alignment and reading direction are not accounted for here. This
     * positioning assumes left-to-right direction and left aligned text. The final
     * positions get mapped later with setAlignmentAndReadingDirection.
     */
    #setPosition() {
        if (this.text === '') {
            return;
        }

        const baselineOffset = this.baselineSeparation;

        // Position the baseline for the first line
        let yBaseline = this.lines[0].ascent;

        // Set position for each line where the origin (0, 0) is the
        // upper left corner of the text asset
        this.#textLineList.forEach(line => {
            // Offset the line so the left edge of the bounding box is at 0
            const originX = -line.bearingX;
            line.setPosition(originX, yBaseline);
            // Using the max baseline distance ensures a consistent baseline to baseline spacing
            // and that taller lines don't overlap with shorter ones
            yBaseline += baselineOffset;
        });
    }

    /**
     * After the initial left-to-right and left aligned position has been set, this modifies the
     * positions to account for the desired reading direction and horizontal alignment.
     */
    #setAlignmentAndReadingDirection() {
        const maxLineWidth = this.maxLineWidth;
        this.#textLineList.forEach(line => {
            line.setAlignmentAndReadingDirection(maxLineWidth);
        });
    }

    /**
     * Caps the number of lines based on maxHeight defined in the style.
     */
    #applyMaxHeight() {
        if (this.text === '') {
            return;
        }

        const cappedLines = [];
        const currentBounds = Rectangle.EMPTY;
        this.#textLineList.forEach(line => {
            currentBounds.enlarge(line.getBounds());
            if (currentBounds.height <= this.style.maxTextHeight) {
                cappedLines.push(line);
            }
            else {
                this.#canDisplayAllText = false;
            }
        });
        this.#textLineList = cappedLines;
    }

    /**
     * This synchronously creates TextAssetMetrics assuming all required fonts have loaded. If a
     * font has not loaded expect an error to be thrown.
     */
    static create(wordTokens: WordToken[], wordWrap: boolean, wordWrapWidth: number, wordElementDataStore: WordElementDataStore): TextAssetMetrics {
        const textWordMetrics = createTextWordMetrics(wordTokens, wordElementDataStore);
        const lineMetrics = layoutTextWords(textWordMetrics, wordElementDataStore, wordWrap, wordWrapWidth);
        return new TextAssetMetrics(lineMetrics);
    }
}
